'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const {
    Component
} = React;
const classnames = require('classnames');
const LazyLoad = require('react-lazy-load').default;

const MediaQuery = require('../../../global/components/MediaQuery');
const {queries} = require('../../../global/core/commonjs/screenSize');
const deviceTypeEnum = require('../../../global/utils/deviceTypeEnum');

const ModalCarouselTracking = require('./ModalCarouselTracking');
const PdpActions = require('../../actions/PdpActions');
const ModalCarousel = require('./ModalCarousel');
const CarouselItem = require('./CarouselItem');
const {ArrowCarousel, SwipeCarousel} = require('../../../global/components/carousel');
const MobileZoomModal = require('./MobileZoomModal');
const ZoomModal = require('./ZoomModal');
const pdpTrackingActions = require('../../actions/pdpTrackingActions.es');

/**
 * Carousel for the PDP hero.
 * @class
 */
class Carousel extends Component {

    constructor(props) {
        super(props);
        this._handleImageClick = this._handleImageClick.bind(this);
        this._handleImageChange = this._handleImageChange.bind(this);
        this._onThumbnailClick = this._onThumbnailClick.bind(this);
        this._updatePage = this._updatePage.bind(this);
        this._closeModal = this._closeModal.bind(this);
        this._onZoomModalLeftArrowClick = this._onZoomModalLeftArrowClick.bind(this);
        this._onZoomModalRightArrowClick = this._onZoomModalRightArrowClick.bind(this);

        this._tracking = new ModalCarouselTracking({
            owner: props.owner,
            imagesLength: props.images.length
        });

        this.state = {
            index: 0,
            modalOpen: props.isStaticModal
        };
    }

    componentWillMount() {
        if (this.props.isStaticModal) {
            this._tracking.showModal(this.state.index);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // fire event only when opening the modal
        if (this.state.modalOpen && !prevState.modalOpen) {
            this._tracking.showModal(this.state.index);
        }
    }

    _handleImageClick(startingIndex) {
        // Re-use the modal if it's already been opened before.
        if (!this.modalCarousel) {
            const {item, images, siteProtocol, socialData} = this.props;
            this.modalCarousel = (new ModalCarousel({
                model: item,
                // Update this carousel's page if the modal carousel changes pages.
                updateImageCallback: this.refs.inner.setPage,
                // The modal is a backbone view so we need to pass the flux context to it in order to render react
                // subviews within.
                context: this.context,
                siteProtocol,
                socialData,
                images
            })).render();
        }

        // Make sure the modal carousel displays the same image as this one by passing in the current page number.
        this.modalCarousel.show(startingIndex);
    }

    _handleImageChange(index) {
        // Fire an event on page change in case other parts of the page need to respond.
        this.context.executeAction(PdpActions.setIndex, {index});
        // Re-render when the page changes.
        this.setState({index});
    }

    _getChildren(deviceType) {
        const {images, title} = this.props;
        const className = classnames('pdp-hero-carousel-image', {
            'is-zoom': !deviceType.isMobile()
        });

        return images.map((image, index) => {
            const imageProps = {
                alt: `${title} ${index + 1}`,
                className,
                src: image.large.src,
                index,
                onClick: () => this.setState({modalOpen: true})
            };

            if (index === 0) {
                return (
                    <CarouselItem {...imageProps} key={index} />
                );
            }

            return (
                <LazyLoad className="pdp-hero-carousel-lazy" offsetHorizontal={300} key={index}>
                    <CarouselItem {...imageProps} />
                </LazyLoad>
            );
        });
    }

    _getCarousel(deviceType) {
        const { style, index } = this.props;
        const ref = 'inner';
        const itemsPerPage = 1;
        const onPageChange = this._handleImageChange;
        const classNames = {list: 'pdp-hero-carousel-list'};
        let props = {};
        let carouselType;

        if (window &&
            'ontouchstart' in window &&
            (deviceType.isMobile() || deviceType.isTablet())) {
            carouselType = SwipeCarousel;

        } else {
            carouselType = ArrowCarousel;
            props = {
                onRightArrowClick: this._onRightArrowClick.bind(this),
                onLeftArrowClick: this._onLeftArrowClick.bind(this)
            };
        }

        return React.createElement(carouselType, Object.assign({}, props, {
            children: this._getChildren(deviceType),
            style,
            ref,
            itemsPerPage,
            classNames,
            onPageChange,
            startingPage: index
        }));
    }

    _onRightArrowClick(page) {
        this.context.executeAction(pdpTrackingActions.trackNextImageClick, {
            image: page + 1, // `page` is zero-indexed
            totalImages: this.props.images.length
        });
    }

    _onLeftArrowClick(page) {
        this.context.executeAction(pdpTrackingActions.trackPreviousImageClick, {
            image: page + 1, // `page` is zero-indexed
            totalImages: this.props.images.length
        });
    }

    _onZoomModalRightArrowClick(page) {
        this._tracking.nextClick(page);
    }

    _onZoomModalLeftArrowClick(page) {
        this._tracking.previousClick(page);
    }

    _onThumbnailClick(page) {
        this._tracking.thumbnailClick(page);
    }

    _updatePage(page) {
        this.refs.inner.setPage(page);
    }

    _closeModal() {
        const { onCloseModal } = this.props;
        if (onCloseModal) {
            onCloseModal();
        } else {
            this.setState({modalOpen: false});
        }
    }

    render() {
        const { index, modalOpen } = this.state;
        const {
            context, owner, wrapperClasses, images, vertical, itemId, itemPk, title, heartComponent,
            siteProtocol, socialData, pinterest, style, mobileStyle, stfOnClick, isPrintPage
        } = this.props;

        return (
            <div className="pdp-hero-carousel" data-tn="pdp-image-carousel">
                {/* If we are on a touch device, use the swiping version of the carousel. */}
                {/* If we are on mobile, we don't open the modal on click. */}
                <MediaQuery query={[queries.lg, queries.xl]} exclude>
                    {this._getCarousel(deviceTypeEnum.mobile())}
                </MediaQuery>
                {/* If we are on a tablet device, use the swiping version of the carousel. */}
                <MediaQuery query={queries.lg}>
                    {this._getCarousel(deviceTypeEnum.tablet())}
                </MediaQuery>
                {/* If we are on desktop, use the version of the carousel with arrows. */}
                <MediaQuery query={queries.xl}>
                    {this._getCarousel(deviceTypeEnum.desktop())}
                </MediaQuery>
                <span className="pdp-hero-carousel-indicator">
                     {`${index + 1} of ${images.length} images`}
                </span>
                {!isPrintPage &&
                    <MediaQuery query={[queries.lg, queries.xl]} exclude>
                        <MobileZoomModal
                            owner={owner}
                            isOpen={modalOpen}
                            style={mobileStyle || style}
                            images={images}
                            startingIndex={index}
                            onIndexChange={this._updatePage}
                            onRequestClose={this._closeModal}
                        />
                    </MediaQuery>
                }
                {!isPrintPage &&
                    <MediaQuery query={[queries.lg, queries.xl]}>
                        <ZoomModal
                            context={context}
                            owner={owner}
                            heartComponent={heartComponent}
                            wrapperClasses={wrapperClasses}
                            overlayClassName="modal-carousel-overlay"
                            isOpen={modalOpen}
                            pinterest={pinterest}
                            style={style}
                            vertical={vertical}
                            itemId={itemId}
                            siteProtocol={siteProtocol}
                            socialData={socialData}
                            itemPk={itemPk}
                            title={title}
                            images={images}
                            startingIndex={index}
                            stfOnClick={stfOnClick}
                            onThumbnailClick={this._onThumbnailClick}
                            onIndexChange={this._updatePage}
                            onRequestClose={this._closeModal}
                            onLeftArrowClick={this._onZoomModalLeftArrowClick}
                            onRightArrowClick={this._onZoomModalRightArrowClick}
                        />
                    </MediaQuery>
                }
            </div>
        );
    }
}

Carousel.propTypes = {
    context: PropTypes.object,
    owner: PropTypes.string,
    heartComponent: PropTypes.object,
    wrapperClasses: PropTypes.string,
    isStaticModal: PropTypes.bool,
    style: PropTypes.object,
    mobileStyle: PropTypes.object,
    socialData: PropTypes.object,
    pinterest: PropTypes.object,
    images: PropTypes.array.isRequired,
    item: PropTypes.object.isRequired,
    itemId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    siteProtocol: PropTypes.string.isRequired,
    vertical: PropTypes.string.isRequired,
    itemPk: PropTypes.string.isRequired,
    stfOnClick: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func,
    isPrintPage: PropTypes.bool,
    index: PropTypes.number.isRequired
};

Carousel.defaultTypes = {
    isStaticModal: false
};

Carousel.contextTypes = {
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired
};

module.exports = Carousel;



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Carousel/Carousel.jsx